import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Field, FormikErrors, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { Checkbox, Divider, FormControlLabel, IconButton } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { Visibility, WhatsApp } from "@mui/icons-material";
import SendWhatsappModal from "./SendWhatsappModal";
import {
  BaseExpenseFieldsProps,
  PetrolPump,
  ExpenseFormData,
  ExpenseEntry,
} from "./types";
import { petrolPumpApi } from "../../../services/api";
import {
  formatCurrency,
  formatDate,
  formatInputCurrency,
} from "../../../utils/utils";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { masterDataAllocationApi } from "../../../project/project_module/services/apiService";
import { useParams } from "react-router-dom";
import { FuelCardType } from "../../masterData/fuelCard/types";
import { fuelCardApiService } from "../../masterData/fuelCard/apiService";

const FUEL_TYPES = ["Petrol", "Diesel", "CNG", "Electric"] as const;

// Debounce hook for search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function FuelExpenseFields({
  index,
  isProjectExpense = false,
}: BaseExpenseFieldsProps) {
  const { projectId, expenseId } = useParams();
  const { values, setFieldValue, errors, touched } =
    useFormikContext<ExpenseFormData>();
  const [petrolPumps, setPetrolPumps] = useState<PetrolPump[]>([]);
  const [selectedPump, setSelectedPump] = useState<PetrolPump | null>(null);
  const [pricePerLitre, setPricePerLitre] = useState<string>(
    values.expenses[index]?.pricePerLitre || ""
  );
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);
  const [displayMasterData, setDisplayMasterData] = useState<boolean>(false);
  const [addNotes, setAddNotes] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;
  const [fuelCards, setFuelCards] = useState<FuelCardType[]>([]);
  const [selectedFuelCard, setSelectedFuelCard] = useState<FuelCardType | null>(
    null
  );
  const [isFuelCard, setIsFuelCard] = useState<boolean>(
    Boolean(
      values.expenses[index].isFuelCard || values.expenses[index].fuelCardId
    )
  );

  // Validation state for odometer readings
  const [odometerValidation, setOdometerValidation] = useState<{
    isValid: boolean;
    error: string;
  }>({ isValid: true, error: "" });
  const [showSendWhatsappModal, setShowSendWhatsappModal] =
    useState<boolean>(false);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  // Memoize latest odometer reading calculation
  const latestOdometerData = useMemo(() => {
    const expenseMap = values.odometerReadings;
    const expense = values.expenses[index];
    if (!expense?.expenseDate || !expenseMap) {
      return { reading: null, fuelQuantity: null, readingDate: null };
    }

    const currentExpenseDate = new Date(expense.expenseDate);
    let latestReading: {
      reading: number;
      fuelQuantity: number;
      readingDate: string;
    } | null = null;

    // Convert Map to array and iterate to find the latest reading
    // that occurred on or before the current expense date (excluding current expense)
    const mapEntries = Array.from(expenseMap);

    for (let i = 0; i < mapEntries.length; i++) {
      const [key, reading] = mapEntries[i];
      const readingDate = new Date(reading.readingDate);

      // Skip readings that are:
      // 1. After the current expense date
      // 2. Same date and same or higher index (to avoid using future entries)
      // 3. Same odometer reading skip if equal to current odometer
      if (readingDate > currentExpenseDate) {
        continue;
      }

      if (
        readingDate.getTime() === currentExpenseDate.getTime() &&
        (reading.index >= index ||
          reading.odometerReading === parseInt(expense.odometerReading || "0"))
      ) {
        continue;
      }

      // Check if this is the latest reading so far
      if (!latestReading) {
        latestReading = {
          reading: reading.odometerReading,
          fuelQuantity: reading.fuelQuantity,
          readingDate: reading.readingDate,
        };
      } else {
        const latestReadingDate = new Date(latestReading.readingDate);

        // Compare by date first, then by odometer reading if same date
        if (
          readingDate > latestReadingDate ||
          (readingDate.getTime() === latestReadingDate.getTime() &&
            reading.odometerReading > latestReading.reading)
        ) {
          latestReading = {
            reading: reading.odometerReading,
            fuelQuantity: reading.fuelQuantity,
            readingDate: reading.readingDate,
          };
        }
      }
    }

    if (latestReading) {
      return {
        reading: latestReading.reading,
        fuelQuantity: latestReading.fuelQuantity,
        readingDate: latestReading.readingDate,
      };
    }

    return { reading: null, fuelQuantity: null, readingDate: null };
  }, [values.odometerReadings, values.expenses[index]?.expenseDate, index]);

  // Memoize odometer difference calculation
  const odometerDifference = useMemo(() => {
    const currentReading = values.expenses[index]?.odometerReading;
    if (currentReading && latestOdometerData.reading !== null) {
      const current = parseInt(currentReading);
      if (!isNaN(current)) {
        return current - latestOdometerData.reading;
      }
    }
    return null;
  }, [
    values.expenses[index]?.odometerReading,
    latestOdometerData.reading,
    index,
  ]);

  // Memoize validation function using global odometer map
  const validateOdometerSequence = useCallback(
    (expenseDate: string, currentReading: number, currentIndex: number) => {
      if (!values.odometerReadings) {
        return { isValid: true, error: "" };
      }

      const odometerMap = values.odometerReadings;
      const currentExpenseDate = new Date(expenseDate);

      let duplicateFound = false;
      let duplicateDate = "";

      let minFutureReading = Infinity;
      let minFutureDate = "";

      const mapEntries = Array.from(odometerMap);

      for (let i = 0; i < mapEntries.length; i++) {
        const [key, reading] = mapEntries[i];
        const readingDate = new Date(reading.readingDate);

        // Skip current entry
        if (key === values.expenses[index]?.id) {
          continue;
        }

        // Duplicate check
        if (
          reading.odometerReading === currentReading &&
          key !== values.expenses[index]?.id
        ) {
          duplicateFound = true;
          duplicateDate = reading.readingDate;
        }

        // Future entry check
        if (
          readingDate > currentExpenseDate ||
          (readingDate.getTime() === currentExpenseDate.getTime() &&
            reading.index > currentIndex &&
            key !== values.expenses[index]?.id)
        ) {
          if (reading.odometerReading < minFutureReading) {
            minFutureReading = reading.odometerReading;
            minFutureDate = reading.readingDate;
          }
        }
      }

      // Check for duplicates
      if (duplicateFound) {
        return {
          isValid: false,
          error: `Odometer reading ${currentReading} already exists on ${duplicateDate}`,
        };
      }

      // Use latestOdometerData.reading for previous entry validation
      if (
        latestOdometerData.reading !== null &&
        currentReading <= latestOdometerData.reading
      ) {
        return {
          isValid: false,
          error: `Odometer reading must be greater than ${latestOdometerData.reading} (from ${latestOdometerData.readingDate})`,
        };
      }

      // Check future validation
      if (minFutureReading !== Infinity && currentReading >= minFutureReading) {
        return {
          isValid: false,
          error: `Odometer reading must be less than ${minFutureReading} (from ${minFutureDate})`,
        };
      }

      return { isValid: true, error: "" };
    },
    [values.odometerReadings, latestOdometerData, values.expenses, index]
  );

  // Combined effect for odometer validation
  useEffect(() => {
    const expense = values.expenses[index];
    if (expense?.expenseDate) {
      const odometerReading = parseInt(expense.odometerReading || "0");

      if (!isNaN(odometerReading) && odometerReading > 0) {
        // Validate the reading
        const validation = validateOdometerSequence(
          expense.expenseDate,
          odometerReading,
          index
        );
        setOdometerValidation(validation);
      } else {
        // Reset validation if odometer reading is invalid/empty
        setOdometerValidation({ isValid: true, error: "" });
      }
    }
  }, [
    values.expenses[index]?.expenseDate,
    values.expenses[index]?.odometerReading,
    values.expenses[index]?.liters,
    index,
  ]);

  // Effect to update fuel card payment amount and date when expense changes
  useEffect(() => {
    const expense = values.expenses[index];
    if (
      expense?.fuelCardId &&
      expense.payments &&
      expense.payments.length > 0
    ) {
      const fuelCardPayment = expense.payments.find(
        (payment) => payment.mode === "FUEL_CARD"
      );
      if (fuelCardPayment) {
        let shouldUpdate = false;
        let updatedPayment = { ...fuelCardPayment };

        // Update amount if it doesn't match
        if (fuelCardPayment.amount !== Number(expense.amount)) {
          updatedPayment.amount = Number(expense.amount) || 0;
          shouldUpdate = true;
        }

        // Update date if it doesn't match
        if (fuelCardPayment.date !== expense.expenseDate) {
          updatedPayment.date =
            expense.expenseDate || new Date().toISOString().split("T")[0];
          shouldUpdate = true;
        }

        if (shouldUpdate) {
          const updatedPayments = expense.payments.map((payment) =>
            payment.mode === "FUEL_CARD" ? updatedPayment : payment
          );
          setFieldValue(`expenses.${index}.payments`, updatedPayments);
        }
      }
    }
  }, [
    values.expenses[index]?.amount,
    values.expenses[index]?.expenseDate,
    values.expenses[index]?.fuelCardId,
    index,
    setFieldValue,
  ]);

  // Cleanup effect to reset validation when component unmounts or expense is removed
  useEffect(() => {
    return () => {
      // Clear validation state for this expense index when component unmounts
      setOdometerValidation({ isValid: true, error: "" });
    };
  }, [index, setFieldValue]);

  // Memoize petrol pump search function
  const searchPetrolPumps = useCallback(
    async (term: string) => {
      if (term.length < 2 && term.length > 0) return; // Don't search for very short terms

      setIsLoading(true);
      setSearchError("");

      try {
        if (isProjectExpense) {
          const pumps =
            await masterDataAllocationApi.getMasterDataAllocationsByProject(
              projectId!,
              "petrolpump"
            );
          setPetrolPumps(pumps?.map((pump) => pump.masterData) as PetrolPump[]);
        } else {
          const pumps = await petrolPumpApi.search(term);
          setPetrolPumps(pumps);
        }
      } catch (error) {
        setSearchError("Failed to load petrol pumps");
        console.error("Error searching petrol pumps:", error);
        setPetrolPumps([]);
      } finally {
        setIsLoading(false);
      }
    },
    [isProjectExpense, projectId]
  );

  const searchFuelCards = useCallback(
    async (term: string, vehicleNumber: string) => {
      if (term.length < 2 && term.length > 0) return; // Don't search for very short terms

      setIsLoading(true);
      setSearchError("");

      try {
        const fuelCards = await fuelCardApiService.search(term, vehicleNumber);
        setFuelCards(fuelCards);
      } catch (error) {
        setSearchError("Failed to load fuel cards");
        console.error("Error searching fuel cards:", error);
        setFuelCards([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Initial load
  useEffect(() => {
    const vehicleNumber =
      values.vehicle?.vehicleNumber || values?.vehicleNumber || "";
    searchFuelCards("", vehicleNumber);
    searchPetrolPumps("");
  }, [searchPetrolPumps, searchFuelCards]);

  // Effect to search when debounced term changes
  useEffect(() => {
    const vehicleNumber =
      values.vehicle?.vehicleNumber || values?.vehicleNumber || "";
    searchFuelCards(debouncedSearchTerm, vehicleNumber);

    searchPetrolPumps(debouncedSearchTerm);
  }, [debouncedSearchTerm, searchPetrolPumps, searchFuelCards]);

  // Initialize state from form values (for edit mode)
  useEffect(() => {
    const expense = values.expenses[index];
    if (expense) {
      if (values?.vehicle?.fuelType) {
        setFieldValue(`expenses.${index}.fuelType`, values.vehicle?.fuelType);
      }
      // Initialize manual entry mode if petrolPumpId is empty but paidTo is filled
      if (
        (!expense.petrolPumpId || expense.petrolPumpId === "") &&
        expense.paidTo
      ) {
        setIsManualEntry(true);
      }

      // Try to find and set selected pump if petrolPumpId exists
      if (expense.petrolPumpId && petrolPumps.length > 0) {
        const pump = petrolPumps.find((p) => p.id === expense.petrolPumpId);
        if (pump) {
          setSelectedPump(pump);
          setIsManualEntry(false);
          setFieldValue(`expenses.${index}.paidTo`, pump.petrolPumpName);
        }
      }
      if (expense.fuelCardId && fuelCards.length > 0) {
        const fuelCard = fuelCards.find((f) => f.id === expense.fuelCardId);
        if (fuelCard) {
          setSelectedFuelCard(fuelCard);
          setIsManualEntry(false);
        }
      }
    }
  }, [
    values.expenses,
    index,
    petrolPumps,
    fuelCards,
    values.vehicle?.fuelType,
    setFieldValue,
  ]);

  // Memoize event handlers
  const handlePetrolPumpChange = useCallback(
    (pump: PetrolPump | null, inputValue?: string) => {
      //set isEdited to true
      setFieldValue(`expenses.${index}.isEdited`, true);
      setSelectedPump(pump);

      if (pump) {
        // Auto-fill fields from selected petrol pump
        setIsManualEntry(false);
        setFieldValue(`expenses.${index}.isManualEntry`, false);
        setFieldValue(`expenses.${index}.petrolPumpId`, pump.id);
        setFieldValue(`expenses.${index}.paidTo`, pump.petrolPumpName);
        setFieldValue(`expenses.${index}.location`, pump.location);
        setFieldValue(
          `expenses.${index}.contactNumber`,
          pump.primaryContactNumber
        );
        setFieldValue(
          `expenses.${index}.bulkDiscountApplied`,
          pump.bulkDiscountPerLitre.toString()
        );
      } else if (inputValue && inputValue.trim() !== "") {
        // Manual entry mode - generate UUID and set manual entry flag
        setIsManualEntry(true);
        const newUUID = uuidv4();
        setFieldValue(`expenses.${index}.isManualEntry`, true);
        setFieldValue(`expenses.${index}.petrolPumpId`, newUUID);
        setFieldValue(`expenses.${index}.paidTo`, inputValue.trim());
        setFieldValue(`expenses.${index}.bulkDiscountApplied`, "0");
      } else {
        // Clear all fields
        setIsManualEntry(false);
        setFieldValue(`expenses.${index}.isManualEntry`, false);
        setFieldValue(`expenses.${index}.petrolPumpId`, "");
        setFieldValue(`expenses.${index}.paidTo`, "");
        setFieldValue(`expenses.${index}.location`, "");
        setFieldValue(`expenses.${index}.contactNumber`, "");
        setFieldValue(`expenses.${index}.bulkDiscountApplied`, "");
      }
    },
    [index, setFieldValue]
  );

  const handleFuelCardChange = useCallback(
    (fuelCard: FuelCardType | null, inputValue?: string) => {
      setFieldValue(`expenses.${index}.isEdited`, true);
      setSelectedFuelCard(fuelCard);

      if (fuelCard) {
        setFieldValue(`expenses.${index}.fuelCardId`, fuelCard.id);
      } else {
        setFieldValue(`expenses.${index}.fuelCardId`, null);
      }
    },
    [index, setFieldValue]
  );

  // Helper function to calculate reverse values
  const handleCalculation = useCallback(
    (fieldName: string, value: string) => {
      const liters = parseFloat(values.expenses[index]?.liters || "0");
      const price = parseFloat(values.expenses[index]?.pricePerLitre || "0");
      const discount = parseFloat(
        values.expenses[index]?.bulkDiscountApplied || "0"
      );
      const amount = parseFloat(values.expenses[index]?.amount || "0");

      if (fieldName === "amount") {
        const newAmount = parseFloat(value || "0");
        // If user enters amount and we have liters, calculate price
        if (newAmount > 0 && liters > 0) {
          const calculatedPrice = newAmount / liters;
          setFieldValue(
            `expenses.${index}.pricePerLitre`,
            calculatedPrice.toFixed(2)
          );
        }
        // If user enters amount and we have price, calculate liters
        else if (newAmount > 0 && price > 0) {
          const calculatedLiters = newAmount / price;
          setFieldValue(
            `expenses.${index}.liters`,
            calculatedLiters.toFixed(2)
          );
        }
      } else if (fieldName === "liters") {
        const newLiters = parseFloat(value || "0");
        // If user enters liters and we have amount, calculate price
        if (newLiters > 0 && amount > 0) {
          const calculatedPrice = amount / newLiters;
          setFieldValue(
            `expenses.${index}.pricePerLitre`,
            calculatedPrice.toFixed(2)
          );
        } else if (newLiters > 0 && price > 0) {
          const calculatedAmount = newLiters * price;
          setFieldValue(
            `expenses.${index}.amount`,
            calculatedAmount.toFixed(2)
          );
        }
      } else if (fieldName === "pricePerLitre") {
        const newPrice = parseFloat(value || "0");

        if (newPrice > 0 && liters > 0) {
          const calculatedAmount = newPrice * liters;
          setFieldValue(
            `expenses.${index}.amount`,
            calculatedAmount.toFixed(2)
          );
        } else if (newPrice > 0 && amount > 0) {
          const calculatedLiters = amount / newPrice;
          setFieldValue(
            `expenses.${index}.liters`,
            calculatedLiters.toFixed(2)
          );
        }
      }
    },
    [values.expenses, index, setFieldValue]
  );

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        // Check if there's already a fuel card payment
        const existingPayments = values.expenses[index].payments || [];
        const hasFuelCardPayment = existingPayments.some(
          (payment) => payment.mode === "FUEL_CARD" && !payment.isDeleted
        );

        if (hasFuelCardPayment) {
          // If fuel card payment exists, don't add another payment
          return;
        }

        setFieldValue(`expenses.${index}.payments`, [
          ...existingPayments,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        // Only clear non-fuel card payments
        const existingPayments = values.expenses[index].payments || [];
        const fuelCardPayments = existingPayments.filter(
          (payment) => payment.mode === "FUEL_CARD"
        );
        setFieldValue(`expenses.${index}.payments`, fuelCardPayments);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  // Function to update global odometer readings map
  const updateOdometerReadingsMap = useCallback(
    (
      expenseIndex: number,
      odometerReading: number,
      expenseDate: string,
      fuelQuantity: number = 0
    ) => {
      if (
        !values.odometerReadings ||
        !expenseDate ||
        !odometerReading ||
        odometerReading <= 0
      ) {
        return;
      }
      const expense = values.expenses[expenseIndex];
      const currentMap = new Map(values.odometerReadings);
      const key = expense.id || `${expenseDate}_${odometerReading}`;

      // Add the new reading
      const readingObj = {
        index: expenseIndex,
        odometerReading,
        fuelQuantity,
        readingDate: expenseDate,
      };

      currentMap.set(key, readingObj);

      // Update the global map
      setFieldValue("odometerReadings", currentMap);
    },
    [values.odometerReadings, setFieldValue]
  );

  // Handler for odometer reading field blur
  const handleOdometerReadingBlur = useCallback(
    (value: string) => {
      const odometerReading = parseInt(value);
      const expense = values.expenses[index];

      if (
        !isNaN(odometerReading) &&
        odometerReading > 0 &&
        expense?.expenseDate
      ) {
        const fuelQuantity = parseFloat(expense.liters || "0");
        updateOdometerReadingsMap(
          index,
          odometerReading,
          expense.expenseDate,
          fuelQuantity
        );
      }
    },
    [index, values.expenses, updateOdometerReadingsMap]
  );

  const handleFromFuelCardCheckbox = useCallback(
    (e: any) => {
      const isFuelCard = e?.target?.checked;
      setFieldValue(`expenses.${index}.isEdited`, true);

      if (isFuelCard) {
        // Set fuelCardId to indicate this is a fuel card expense
        setIsFuelCard(true);
        setFieldValue(`expenses.${index}.fuelCardId`, null);
        setFieldValue(`expenses.${index}.isFuelCard`, true);
      } else {
        // Clear fuelCardId to indicate this is not a fuel card expense
        setIsFuelCard(false);
        setFieldValue(`expenses.${index}.fuelCardId`, null);
        setFieldValue(`expenses.${index}.isFuelCard`, false);
      }

      setSelectedPump(null);
      setSelectedFuelCard(null);
      setIsManualEntry(false);
      // Clear search term to reset the autocomplete
      setSearchTerm("");
      // Reset display master data
      setDisplayMasterData(false);

      if (isFuelCard) {
        // Create a payment record with FUEL_CARD mode
        const fuelCardPayment = {
          type: "SETTLEMENT",
          mode: "FUEL_CARD",
          date:
            values.expenses[index].expenseDate ||
            new Date().toISOString().split("T")[0],
          location: values.expenses[index].location || "",
          receivedFrom: "",
          remarks: "",
          status: "PAID",
          isEdited: false,
          isDeleted: false,
          isNew: true,
          amount: values.expenses[index].amount || "0",
        };

        // Set the payment record (this will be hidden from UI but stored in form)
        setFieldValue(`expenses.${index}.payments`, [fuelCardPayment]);
      } else {
        // If payment has an id, mark as deleted; else remove it from the array
        const existingPayments = values.expenses[index].payments || [];
        const updatedPayments = existingPayments
          .map((payment) =>
            payment.mode === "FUEL_CARD" && payment.id
              ? { ...payment, isDeleted: true, isEdited: true }
              : payment
          )
          .filter((payment) => !(payment.mode === "FUEL_CARD" && !payment.id));
        setFieldValue(`expenses.${index}.payments`, updatedPayments);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  const containsPayment = useMemo(() => {
    const payments = values.expenses[index].payments || [];
    return payments.some((payment) => !payment.isDeleted);
  }, [values.expenses, index]);

  return (
    <>
      <Grid
        container
        item
        xs={12}
        sm={9}
        spacing={1.5}
        sx={{ alignSelf: "flex-start" }}
      >
        <Grid container item xs={12} sm={12} spacing={1}>
          {/* Quantity and Price per Litre */}
          <Grid item xs={4} sm={2.5}>
            <Field name={`expenses.${index}.liters`}>
              {({ field, meta }: any) => (
                <TextField
                  {...field}
                  {...commonTextFieldProps}
                  label="Quantity (Litres)"
                  type="number"
                  fullWidth
                  required
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ""}
                  value={field.value || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, "");
                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                      field.onChange({
                        target: {
                          name: field.name,
                          value: value,
                        },
                      });
                      setFieldValue(`expenses.${index}.isEdited`, true);
                      // Handle reverse calculation if amount exists
                      handleCalculation("liters", value);
                    }
                  }}
                  onBlur={(e) => {
                    // Update odometer readings map when fuel quantity changes
                    const expense = values.expenses[index];
                    const odometerReading = parseInt(
                      expense?.odometerReading || "0"
                    );
                    if (
                      !isNaN(odometerReading) &&
                      odometerReading > 0 &&
                      expense?.expenseDate
                    ) {
                      const fuelQuantity = parseFloat(e.target.value || "0");
                      updateOdometerReadingsMap(
                        index,
                        odometerReading,
                        expense.expenseDate,
                        fuelQuantity
                      );
                    }
                  }}
                  inputProps={{
                    step: "0.01",
                    min: "0",
                    inputMode: "decimal",
                  }}
                />
              )}
            </Field>
          </Grid>

          <Grid item xs={4} sm={3}>
            <Field name={`expenses.${index}.amount`}>
              {({ field, meta }: any) => (
                <TextField
                  {...field}
                  {...commonTextFieldProps}
                  label="Total Amount"
                  fullWidth
                  required
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ""}
                  value={field.value ? formatInputCurrency(field.value) : ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, "");
                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                      field.onChange({
                        target: {
                          name: field.name,
                          value: value,
                        },
                      });
                      setFieldValue(`expenses.${index}.isEdited`, true);
                      // Handle reverse calculation if liters exist
                      handleCalculation("amount", value);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        component="span"
                        sx={{ mr: 0.5, fontSize: "0.75rem" }}
                      >
                        ₹
                      </Typography>
                    ),
                  }}
                  inputProps={{
                    inputMode: "decimal",
                  }}
                />
              )}
            </Field>
          </Grid>

          <Grid item xs={4} sm={2}>
            <Field name={`expenses.${index}.pricePerLitre`}>
              {({ field, meta }: any) => (
                <TextField
                  {...field}
                  {...commonTextFieldProps}
                  label="Price per Litre"
                  fullWidth
                  required
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error ? meta.error : ""}
                  value={field.value ? formatInputCurrency(field.value) : ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, "");
                    if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
                      field.onChange({
                        target: {
                          name: field.name,
                          value: value,
                        },
                      });
                      setFieldValue(`expenses.${index}.isEdited`, true);
                      setPricePerLitre(value);
                      // Handle reverse calculation if amount exists
                      handleCalculation("pricePerLitre", value);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        component="span"
                        sx={{ mr: 0.5, fontSize: "0.75rem" }}
                      >
                        ₹
                      </Typography>
                    ),
                  }}
                  inputProps={{
                    inputMode: "decimal",
                  }}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: "1px solid",
                borderColor: "primary.light",
                borderRadius: 1,
                py: 0.2,
                pl: 1.5,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFuelCard}
                    onChange={handleFromFuelCardCheckbox}
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                    }}
                  />
                }
                label="From Fuel Card"
                sx={{
                  p: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.7rem",
                    color: "primary.main",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container item xs={12} sm={12} spacing={1}>
          <Grid item xs={6} sm={2.5}>
            <Field
              as={TextField}
              label="Date"
              type="date"
              name={`expenses.${index}.expenseDate`}
              onChange={(e: any) => {
                setFieldValue(`expenses.${index}.expenseDate`, e.target.value);
                setFieldValue(`expenses.${index}.isEdited`, true);
              }}
              onBlur={(e: any) => {
                // Update odometer readings map when date changes
                const expense = values.expenses[index];
                const odometerReading = parseInt(
                  expense?.odometerReading || "0"
                );
                if (
                  !isNaN(odometerReading) &&
                  odometerReading > 0 &&
                  e.target.value
                ) {
                  const fuelQuantity = parseFloat(expense?.liters || "0");
                  updateOdometerReadingsMap(
                    index,
                    odometerReading,
                    e.target.value,
                    fuelQuantity
                  );
                }
              }}
              fullWidth
              required
              {...commonDateFieldProps}
            />
          </Grid>

          {isFuelCard && (
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={fuelCards}
                loading={isLoading}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : `${option.accountNumber} - ${option.provider}`
                }
                value={selectedFuelCard || null}
                onChange={(_, newValue, reason) => {
                  if (
                    reason === "selectOption" &&
                    typeof newValue === "object"
                  ) {
                    // Selected from dropdown - reset manual entry mode
                    handleFuelCardChange(newValue);
                  } else if (reason === "clear") {
                    // Clear selection - reset all fields
                    handleFuelCardChange(null);
                  }
                }}
                onInputChange={(_, inputValue, reason) => {
                  setSearchTerm(inputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fuel Card"
                    placeholder="Search and select from master data..."
                    required
                    fullWidth
                    error={!!searchError || !!expenseErrors?.fuelCardId}
                    helperText={
                      searchError || expenseErrors?.fuelCardId
                        ? "Fuel card is required"
                        : ""
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    {...commonTextFieldProps}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ fontSize: "0.75rem", py: 0.5 }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        {option.provider}
                      </Typography>
                      {option.accountNumber && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem", mt: 0.25 }}
                        >
                          {option.accountNumber}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  isLoading
                    ? "Searching..."
                    : searchError
                    ? "Error loading options"
                    : "No matching fuel cards found. Please select from available options."
                }
              />
            </Grid>
          )}

          <Grid
            container
            item
            xs={12}
            sm={4}
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            <Grid item xs={selectedPump && !isManualEntry ? 11 : 12}>
              <Autocomplete
                freeSolo
                options={petrolPumps}
                loading={isLoading}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : `${option.petrolPumpName} ${
                        option.location ? `- ${option.location}` : ""
                      }`
                }
                value={selectedPump || values.expenses[index].paidTo || null}
                onChange={(_, newValue, reason) => {
                  if (
                    reason === "selectOption" &&
                    typeof newValue === "object"
                  ) {
                    // Selected from dropdown - reset manual entry mode
                    setIsManualEntry(false);
                    setFieldValue(`expenses.${index}.isManualEntry`, false);
                    setFieldValue(`expenses.${index}.petrolPumpId`, newValue?.id);
                    setFieldValue(`expenses.${index}.paidTo`, newValue?.petrolPumpName);
                    handlePetrolPumpChange(newValue);
                  } else if (reason === "clear") {
                    // Clear selection - reset all fields
                    setIsManualEntry(false);
                    setFieldValue(`expenses.${index}.isManualEntry`, false);
                    handlePetrolPumpChange(null);
                  }
                }}
                onInputChange={(_, inputValue, reason) => {
                  setSearchTerm(inputValue);

                  if (reason === "input") {
                    // Check if input matches any existing pump
                    const matchingPump = petrolPumps.find(
                      (pump) =>
                        pump.petrolPumpName
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        `${pump.petrolPumpName} - ${pump.location}`.toLowerCase() ===
                          inputValue.toLowerCase()
                    );

                    if (!matchingPump && inputValue.trim() !== "") {
                      // Manual entry - generate UUID and set manual entry flag
                      setIsManualEntry(true);
                      const newUUID = uuidv4();
                      setFieldValue(`expenses.${index}.isEdited`, true);
                      setFieldValue(`expenses.${index}.isManualEntry`, true);
                      setFieldValue(`expenses.${index}.petrolPumpId`, newUUID);
                      setFieldValue(
                        `expenses.${index}.paidTo`,
                        inputValue.trim()
                      );
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Petrol Pump Name"
                    placeholder="Search from master data or enter manually..."
                    required
                    fullWidth
                    error={
                      !!searchError ||
                      (!!expenseErrors?.paidTo &&
                        !!touched.expenses?.[index]?.paidTo)
                    }
                    helperText={
                      searchError ||
                      (expenseErrors?.paidTo &&
                        touched.expenses?.[index]?.paidTo)
                        ? "Petrol pump name is required"
                        : ""
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    {...commonTextFieldProps}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ fontSize: "0.75rem", py: 0.5 }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        {option.petrolPumpName}
                      </Typography>
                      {option.location && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem", mt: 0.25 }}
                        >
                          {option.location}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={
                  isLoading
                    ? "Searching..."
                    : searchError
                    ? "Error loading options"
                    : "No matching petrol pumps found. You can still type to add manually."
                }
              />
            </Grid>
            {selectedPump && !isManualEntry && (
              <Grid
                item
                xs={1}
                sm={1}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <IconButton
                  title={displayMasterData ? "Hide Details" : "View Details"}
                  onClick={() => setDisplayMasterData(!displayMasterData)}
                  sx={{
                    color: "primary.dark",
                    "&:hover": {
                      color: "primary.main",
                    },
                  }}
                >
                  <Visibility sx={{ width: "16px", height: "16px" }} />
                </IconButton>
              </Grid>
            )}
          </Grid>

          {/* Odometer Reading */}
          <Grid item xs={4} sm={4}>
            <Field
              {...commonTextFieldProps}
              as={TextField}
              label="Km's Reading"
              name={`expenses.${index}.odometerReading`}
              onChange={(e: any) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^[0-9]*$/.test(value)) {
                  setFieldValue(`expenses.${index}.isEdited`, true);
                  setFieldValue(`expenses.${index}.odometerReading`, value);
                }
              }}
              onBlur={(e: any) => {
                handleOdometerReadingBlur(e.target.value);
              }}
              type="number"
              fullWidth
              helperText={
                !odometerValidation.isValid
                  ? odometerValidation.error
                  : expenseErrors?.odometerReading
                  ? expenseErrors.odometerReading
                  : ""
              }
              error={
                expenseErrors?.odometerReading || !odometerValidation.isValid
              }
              sx={{
                ...commonTextFieldProps.sx,
                ...(odometerValidation.isValid
                  ? {}
                  : {
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#f57f17", // Yellow border
                        },
                        "&:hover fieldset": {
                          borderColor: "#f57f17",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#f57f17",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#f57f17", // Yellow label
                        "&.Mui-focused": {
                          color: "#f57f17",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#f57f17 !important", // Yellow helper text
                        fontSize: "0.55rem",
                      },
                    }),
              }}
              inputProps={{
                min: "0",
                step: "1",
              }}
            />
          </Grid>

          {/* Manual Entry Fields (when not from master data) */}
          {(isManualEntry ||
            values.expenses[index]?.isManualEntry ||
            (!selectedPump && values.expenses[index]?.paidTo)) && (
            <>
              <Grid item xs={4} sm={4}>
                <Field
                  as={TextField}
                  label="Contact Number"
                  name={`expenses.${index}.contactNumber`}
                  fullWidth
                  placeholder="Enter contact number (optional)"
                  inputProps={{
                    maxLength: 10,
                  }}
                  {...commonTextFieldProps}
                  error={Boolean(
                    expenseErrors?.contactNumber && expenseErrors?.contactNumber
                  )}
                  helperText={
                    expenseErrors?.contactNumber && expenseErrors?.contactNumber
                  }
                />
              </Grid>
              <Grid item xs={4} sm={4}>
                <Field
                  as={TextField}
                  label="Location"
                  name={`expenses.${index}.location`}
                  fullWidth
                  placeholder="Enter petrol pump location"
                  {...commonTextFieldProps}
                />
              </Grid>
            </>
          )}
          {/* Auto-filled Petrol Pump Details (only for master data) */}
          {!isManualEntry &&
            displayMasterData &&
            (selectedPump ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "primary.light",
                    bgcolor: "#F0F7FF",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary.dark"
                    gutterBottom
                  >
                    Petrol Pump Details (From Master Data)
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Contact Person: {selectedPump.primaryContactName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Phone: {selectedPump.primaryContactNumber}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Provider: {selectedPump.providerName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="success.dark">
                        Bulk Discount: ₹{selectedPump.bulkDiscountPerLitre}
                        /Litre
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ) : null)}
        </Grid>
        <Grid item xs={12} sm={12}>
          <Button
            variant="outlined"
            onClick={() => setAddNotes(!addNotes)}
            size={smallButtonProp.size}
            sx={{
              ...smallButtonProp.sx,
              color: "grey",
              borderColor: "grey.400",
              "&:hover": {
                borderColor: "grey.400",
                color: "grey",
              },
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
              {addNotes ? "Hide Notes" : "Add Notes"}
            </Typography>
          </Button>
          {Number(values.expenses[index].amount) > 0 && !containsPayment && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={containsPayment}
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
        </Grid>
        {/* Notes */}
        <Grid item xs={12} sx={{ display: addNotes ? "block" : "none" }}>
          <Field
            as={TextField}
            label="Notes"
            name={`expenses.${index}.notes`}
            onChange={(e: any) => {
              setFieldValue(`expenses.${index}.notes`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            multiline
            rows={2}
            placeholder="Any additional notes about this fuel expense..."
            {...commonTextFieldProps}
          />
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        sm={3}
        spacing={1}
        sx={{ alignSelf: "flex-start" }}
      >
        <Box
          sx={{
            py: 0.5,
            px: 1.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "grey.500",
            bgcolor: "#f5f5f5",
            mt: 1,
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.65rem" }}
            gutterBottom
          >
            Last Km's Reading (
            {formatDate(latestOdometerData.readingDate || "")})
          </Typography>
          {latestOdometerData.reading !== null ? (
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontWeight: "bold", fontSize: "0.65rem" }}
            >
              {latestOdometerData.reading.toLocaleString()} km
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontStyle: "italic" }}
            >
              No previous readings
            </Typography>
          )}

          {odometerDifference !== null && (
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                Difference from last reading
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: "medium", fontSize: "0.65rem" }}
              >
                {odometerDifference.toLocaleString()} km
              </Typography>

              {/* Average Fuel Efficiency */}
              {values.expenses[index].liters &&
                parseFloat(values.expenses[index].liters) > 0 &&
                odometerDifference > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem" }}
                    >
                      Average from last record:
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: "medium", fontSize: "0.65rem" }}
                    >
                      {(
                        odometerDifference /
                        parseFloat(values.expenses[index].liters)
                      ).toFixed(2)}{" "}
                      km/L
                    </Typography>
                    {values.expenses[index].liters &&
                      values.expenses[index].amount &&
                      values.expenses[index].pricePerLitre && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-start",
                            mt: 0.5,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={
                              <WhatsApp
                                sx={{ width: "12px", height: "12px" }}
                              />
                            }
                            color="secondary"
                            sx={{
                              ...smallButtonProp.sx,
                              fontSize: "0.75rem",
                              height: "20px",
                              p: 1,
                            }}
                            onClick={() => setShowSendWhatsappModal(true)}
                          >
                            Send record on Whatsapp
                          </Button>
                        </Box>
                      )}

                    {values.expenses[index].sendWhatsappToDriver && (
                      <>
                        <Divider sx={{ my: 1, borderColor: "grey.300" }} />
                        <Grid item xs={12} sm={12}>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.65rem", my: 1 }}
                          >
                            Details will be sent to following drivers:
                          </Typography>
                          {values.expenses[index].whatsappData?.drivers?.map(
                            (driver: any) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.65rem" }}
                                >
                                  {driver.name} ({driver.contactNumber})
                                </Typography>
                              </Box>
                            )
                          )}
                        </Grid>
                      </>
                    )}
                  </Box>
                )}
            </Box>
          )}
        </Box>
      </Grid>
      <SendWhatsappModal
        open={showSendWhatsappModal}
        onClose={() => setShowSendWhatsappModal(false)}
        projectId={projectId}
        vehicleId={values?.vehicleId}
        lastReadingDate={latestOdometerData.readingDate || ""}
        currentReadingDate={values.expenses[index].expenseDate || ""}
        average={
          odometerDifference && values.expenses[index].liters
            ? odometerDifference / parseFloat(values.expenses[index].liters)
            : 0
        }
        vehicleNumber={values.vehicle?.vehicleNumber || values.vehicleNumber}
        expenseData={values.expenses[index]}
        lastOdometerReading={parseFloat(values.expenses[index].liters) || 0}
        index={index}
      />
    </>
  );
}

export default FuelExpenseFields;
